<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(defineProps<{
  fluorescence?: string | string[];
  mobileMode?: boolean;
}>(), {
  fluorescence: "n",
  mobileMode: false,
});

const host = process.env.VUE_APP_HOST;

const fluorescences = [
  {
    key: "f",
    label: "Medium",
    img: "/img/diamonds/filter/fluorescence/fluo_f@2x.jpg",
  },
  {
    key: "sl",
    label: "Weak",
    img: "/img/diamonds/filter/fluorescence/fluo_sl@2x.jpg",
  },
  {
    key: "n",
    label: "None",
    img: "/img/diamonds/filter/fluorescence/fluo_n@2x.jpg",
  },
];

const getNormalizedFluorescence = (f: string) => {
  if (!f) return "n";
  f = f.toUpperCase();
  if (f === "N" || f === "NONE" || f === "NON" || f === "KEINE") return "n";
  if (f === "SL" || f === "SLIGHT" || f === "WEAK" || f === "SCHWACH" || f === "FAINT") return "sl";
  if (f === "F" || f === "MEDIUM" || f === "MITTEL") return "f";
  return f.toLowerCase();
};

const getInitialIndex = () => {
  const fluorescenceValue = Array.isArray(props.fluorescence)
    ? props.fluorescence[0]
    : props.fluorescence;
  if (!fluorescenceValue) return 2;
  const normalized = getNormalizedFluorescence(fluorescenceValue);
  const index = fluorescences.findIndex((f) => f.key === normalized);
  return index !== -1 ? index : 2;
};

const selectedIndex = ref(getInitialIndex());

const isMobile = ref(window.innerWidth < 769);
const updateMobile = () => {
  isMobile.value = window.innerWidth < 769;
};

const isMobileMode = computed(() => props.mobileMode || isMobile.value);

watch(() => props.fluorescence, () => {
  selectedIndex.value = getInitialIndex();
});

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

const updateIndexFromEvent = (e: MouseEvent | TouchEvent) => {
  if (!sliderRef.value) return;
  const rect = sliderRef.value.getBoundingClientRect();
  const clientX =
    "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  let percentage = offsetX / rect.width;
  percentage = Math.max(0, Math.min(0.99, percentage));
  selectedIndex.value = Math.floor(percentage * fluorescences.length);
};

const startDragging = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true;
  updateIndexFromEvent(e);
};

const handleMove = (e: MouseEvent | TouchEvent) => {
  if (isDragging.value) {
    updateIndexFromEvent(e);
  }
};

const stopDragging = () => {
  isDragging.value = false;
};

onMounted(() => {
  window.addEventListener("mousemove", handleMove);
  window.addEventListener("mouseup", stopDragging);
  window.addEventListener("touchmove", handleMove, { passive: false });
  window.addEventListener("touchend", stopDragging);
  window.addEventListener("resize", updateMobile);
});

onUnmounted(() => {
  window.removeEventListener("mousemove", handleMove);
  window.removeEventListener("mouseup", stopDragging);
  window.removeEventListener("touchmove", handleMove);
  window.removeEventListener("touchend", stopDragging);
  window.removeEventListener("resize", updateMobile);
});
</script>

<template>
  <div :class="['flex flex-col items-center gap-12', isMobileMode ? '' : 'md:flex-row']">
    <!-- Left column: info text -->
    <div :class="['w-full text-left', isMobileMode ? '' : 'md:w-1/3']">
      <slot name="title" :selectedLabel="fluorescences[selectedIndex].label">
        <h3 class="text-lf font-bold mb-6">
          {{ t("components.diamonds.details.fluorescence.title") }}:
          {{ fluorescences[selectedIndex].label }}
        </h3>
      </slot>
      <p class="text-black font-light text-xs leading-relaxed whitespace-pre-line">
        {{ t("components.diamonds.details.fluorescence.text") }}
      </p>
    </div>

    <!-- Right column: Slider -->
    <div :class="['w-full', isMobileMode ? '' : 'md:w-2/3']">
      <div class="flex flex-col">
        <!-- Images -->
        <div :class="['flex justify-center items-end mb-6', isMobileMode ? '' : 'md:justify-between']">
          <div
            v-for="(f, index) in fluorescences"
            :key="f.key"
            class="justify-center px-2"
            :class="[
              selectedIndex === index ? 'flex' : (isMobileMode ? 'hidden' : 'hidden md:flex'),
              isMobileMode ? '' : 'md:flex-1',
            ]"
          >
            <img
              :src="host + f.img"
              :alt="f.label"
              class="w-full transition-opacity duration-300 cursor-pointer"
              :class="[
                isMobileMode ? 'max-w-[420px]' : 'max-w-[420px] md:max-w-[140px]',
                { 'md:opacity-90': !isMobileMode && selectedIndex !== index }
              ]"
              @click="selectedIndex = index"
            />
          </div>
        </div>

        <!-- Slider -->
        <div
          class="relative pb-32 cursor-pointer"
          ref="sliderRef"
          @mousedown="startDragging"
          @touchstart.prevent="startDragging"
        >
          <div class="flex gap-1.5 h-1">
            <div
              v-for="(f, index) in fluorescences"
              :key="'bar-' + f.key"
              class="flex-1 cursor-pointer relative group"
              @click="selectedIndex = index"
            >
              <!-- Active/Inactive Bar -->
              <div
                class="h-full w-full rounded-full transition-colors duration-300"
                :class="
                  selectedIndex === index ? 'bg-brand-main' : 'bg-brand-main/30'
                "
              ></div>

              <!-- Label -->
              <span
                class="absolute top-2 left-1/2 -translate-x-1/2 text-sm transition-colors duration-300 whitespace-nowrap"
                :class="
                  selectedIndex === index
                    ? 'text-brand-main font-medium'
                    : 'text-gray-400'
                "
              >
                {{ f.label }}
              </span>
            </div>
          </div>

          <!-- Description Bubble -->
          <div class="absolute left-0 right-0 mt-10 transition-all duration-300">
            <div class="bg-brand-main rounded-xl p-2 text-white text-center relative">
              <!-- Triangle pointing up -->
              <div
                class="absolute -top-2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-brand-main transition-all duration-300"
                :style="{
                  left: `calc(${selectedIndex * 33.33 + 16.66}% - 10px)`,
                }"
              ></div>
              <p class="text-xs leading-relaxed">
                {{ t("components.diamonds.details.fluorescence.descriptions." + fluorescences[selectedIndex].key) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
